import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { playerService, coachService, goalkeeperService, subscribeToPerformanceRecords, unsubscribeFromPerformanceRecords } from '../../services/performanceService';
import MetricsOverview from './components/MetricsOverview';
import TrendingTalent from './components/TrendingTalent';
import PerformanceCharts from './components/PerformanceCharts';
import RecordUploads from './components/RecordUploads';
import PerformanceInsights from './components/PerformanceInsights';
import LiveMarketUpdates from './components/LiveMarketUpdates';
import LiveTransferAlerts from './components/LiveTransferAlerts';

const UnifiedMetricsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [filterRole, setFilterRole] = useState('all');
  const [timeRange, setTimeRange] = useState('30days');
  
  const [players, setPlayers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [goalkeepers, setGoalkeepers] = useState([]);
  const [recentUploads, setRecentUploads] = useState([]);

  // Fetch all performance data
  useEffect(() => {
    fetchAllData();
  }, []);

  // Real-time synchronization
  useEffect(() => {
    const channel = subscribeToPerformanceRecords((table, payload) => {
      console.log(`Real-time update from ${table}:`, payload);
      // Refresh data on real-time updates
      fetchAllData();
    });

    return () => {
      unsubscribeFromPerformanceRecords(channel);
    };
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [playersRes, coachesRes, goalkeepersRes] = await Promise.all([
        playerService?.getAll(),
        coachService?.getAll(),
        goalkeeperService?.getAll()
      ]);

      setPlayers(playersRes?.data || []);
      setCoaches(coachesRes?.data || []);
      setGoalkeepers(goalkeepersRes?.data || []);

      // Combine and sort by creation date for recent uploads
      const allRecords = [
        ...(playersRes?.data || [])?.map(p => ({ ...p, type: 'player' })),
        ...(coachesRes?.data || [])?.map(c => ({ ...c, type: 'coach' })),
        ...(goalkeepersRes?.data || [])?.map(g => ({ ...g, type: 'goalkeeper' }))
      ]?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt))?.slice(0, 10);

      setRecentUploads(allRecords);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const views = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'trending', label: 'Trending Talent', icon: 'TrendingUp' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'uploads', label: 'Recent Uploads', icon: 'Upload' },
    { id: 'insights', label: 'Insights', icon: 'Lightbulb' },
    { id: 'live', label: 'Live Updates', icon: 'Radio' }
  ];

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'player', label: 'Players' },
    { value: 'coach', label: 'Coaches' },
    { value: 'goalkeeper', label: 'Goalkeepers' }
  ];

  const timeRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' }
  ];

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Unified Metrics Dashboard | ProX11 Market</title>
        <meta name="description" content="Comprehensive analytics dashboard for player, coach, and goalkeeper performance metrics with real-time data visualization" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 border-b border-border pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
                  <Icon name="Activity" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-primary">Real-Time Analytics</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
                  Unified Metrics Dashboard
                </h1>
                <p className="text-base md:text-lg text-muted-foreground max-w-3xl">
                  Comprehensive performance analytics across all talent categories with real-time data visualization and trending insights
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-success animate-pulse" />
                <span className="text-sm text-muted-foreground">Live Data</span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-card border-b border-border sticky top-16 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 overflow-x-auto py-4">
              {views?.map((view) => (
                <button
                  key={view?.id}
                  onClick={() => setActiveView(view?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeView === view?.id
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={view?.icon} size={18} />
                  {view?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-muted/50 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Filter" size={18} />
                <span className="font-medium">Filters:</span>
              </div>
              <div className="flex flex-wrap items-center gap-3 flex-1">
                <div className="w-full sm:w-48">
                  <Select
                    options={roleOptions}
                    value={filterRole}
                    onChange={setFilterRole}
                    placeholder="Select role"
                  />
                </div>
                <div className="w-full sm:w-48">
                  <Select
                    options={timeRangeOptions}
                    value={timeRange}
                    onChange={setTimeRange}
                    placeholder="Select time range"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={fetchAllData}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeView === 'overview' && (
            <MetricsOverview
              players={players}
              coaches={coaches}
              goalkeepers={goalkeepers}
              filterRole={filterRole}
            />
          )}

          {activeView === 'trending' && (
            <TrendingTalent
              players={players}
              coaches={coaches}
              goalkeepers={goalkeepers}
              filterRole={filterRole}
            />
          )}

          {activeView === 'analytics' && (
            <PerformanceCharts
              players={players}
              coaches={coaches}
              goalkeepers={goalkeepers}
              filterRole={filterRole}
            />
          )}

          {activeView === 'uploads' && (
            <RecordUploads
              recentUploads={recentUploads}
              filterRole={filterRole}
            />
          )}

          {activeView === 'insights' && (
            <PerformanceInsights
              players={players}
              coaches={coaches}
              goalkeepers={goalkeepers}
            />
          )}

          {activeView === 'live' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LiveMarketUpdates />
              <LiveTransferAlerts />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UnifiedMetricsDashboard;